import React, { useEffect, useState, useCallback } from "react";
import reqSchema from "../schemas/requirement.json";
import DynamicForm from "../components/DynamicForm/DynamicForm";
import DataTable from "../components/DataTable/DataTable";
import { apiGet, apiPost, apiPut, apiDelete } from "../services/api";
import { Box, Card, CardContent, Button, Typography } from "@mui/material";
import { usePopup } from "../components/PopupProvider";
import { Routes, Route, useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import * as permissions from "./app_props";

export default function RequirementsPage() {
  const user = JSON.parse(sessionStorage.getItem("userInfo"));
  const [reqs, setReqs] = useState([]);
  const SESSION_PAGE_SIZE_KEY = "compliance_ui_page_size";
  const defaultSize = (() => {
    try {
      const s = sessionStorage.getItem(SESSION_PAGE_SIZE_KEY);
      const n = Number(s);
      if (n) return n;
    } catch (e) {
      // ignore
    }
    return 50;
  })();
  const [reqsPage, setReqsPage] = useState({
    page: 0,
    size: defaultSize,
    total: 0,
  });
  const [regulations, setRegulations] = useState([]);
  const [filters, setFilters] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const { showConfirm, showSuccess, showError } = usePopup();

  const buildFilterQuery = useCallback(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.set(`filter.${key}`, value);
      }
    });
    return params.toString();
  }, [filters]);

  const load = useCallback(
    async (page = reqsPage.page, size = reqsPage.size) => {
      try {
        const query = buildFilterQuery();
        const [rpage, crsResp] = await Promise.all([
          apiGet(
            `/api/requirements?page=${page}&size=${size}${query ? `&${query}` : ""}`,
          ),
          apiGet(`/api/crs?page=0&size=1000`),
        ]);
        const content = Array.isArray(rpage) ? rpage : rpage.content || [];
        setReqs(content);
        if (rpage && typeof rpage.totalElements !== "undefined") {
          setReqsPage((cur) => ({
            ...cur,
            total: rpage.totalElements,
            page,
            size,
          }));
        } else {
          setReqsPage((cur) => ({ ...cur, page, size }));
        }

        const crs = Array.isArray(crsResp) ? crsResp : crsResp.content || [];
        setRegulations(crs);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to load requirements or regulations", err);
      }
    },
    [buildFilterQuery, reqsPage.page, reqsPage.size],
  );

  useEffect(() => {
    load();
  }, [load]);

  const handlePageSizeChange = useCallback((newSize) => {
    try {
      sessionStorage.setItem(SESSION_PAGE_SIZE_KEY, String(newSize));
    } catch (e) {
      // ignore
    }
    setReqsPage((p) => ({ ...p, page: 0, size: newSize }));
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setReqsPage((p) => ({ ...p, page: newPage }));
  }, []);

  async function handleSave(formValues) {
    try {
      const general = formValues.general || {};
      const admin = formValues.admin || {};
      const taskTemplateChanged =
        formValues._originalTaskTemplateId &&
        formValues.general.taskTemplate?.id !==
          formValues._originalTaskTemplateId;

      const taskRecommendedChangedToNo =
        formValues._originalTaskRecommended === true &&
        (general.taskRecommended === "No" || general.taskRecommended === false);

      if (
        (taskTemplateChanged || taskRecommendedChangedToNo) &&
        !general.taskRegenerationStrategy
      ) {
        showError("Please select Task Regeneration Strategy");
        return;
      }
      // try to find regulation id for selected source
      let sourceCrs = null;
      if (general.source) {
        // if user selected from dropdown the value is a string (regulationName/title)
        const match = regulations.find(
          (c) =>
            (c.regulationName || c.name || c.title || "") === general.source,
        );
        if (match) sourceCrs = { id: match.id || match._id || match };
      }

      // load locations and facilities to resolve ids from names (we need id for country/state and site)
      let locations = [];
      try {
        const [locResp] = await Promise.all([apiGet("/api/locations")]);
        locations = Array.isArray(locResp) ? locResp : locResp.content || [];
      } catch (e) {
        locations = [];
      }

      const findLocation = (val) => {
        if (!val) return null;
        if (typeof val === "object" && (val.id || val._id))
          return val.id || val._id;
        const name = String(val).trim();
        const loc = locations.find(
          (l) => (l.name || l.code || "").toString() === name,
        );
        return loc ? loc.id || loc._id : null;
      };

      const payload = {
        taskRegenerationStrategy: general.taskRegenerationStrategy || null,
        title: general.summary,
        description: general.details || general.summary,
        citation: general.citation,
        externalSourceLink: general.externalLink,
        details: general.details,
        additionalNotes: general.notes,
        appliesTo: general.appliesTo,
        taskRecommended:
          general.taskRecommended === "Yes" || general.taskRecommended === true,
        roaReferenceId: admin.roaRefId || null,
        recordNo: admin.recordNo,
        recordStatus: admin.recordStatus,
        protectedFlag: !!admin.protected,
        sourceComplianceRegulationStandard: sourceCrs,
        sourceType: admin.sourceType || null,
        sourceRegion: admin.sourceRegion || null,
        sourceCountry: admin.sourceCountry || null,
        area: admin.area || null,
        // compliance method
        complianceMethod:
          (formValues.complianceMethod &&
            formValues.complianceMethod.methodName) ||
          null,
        // NOTE: removed complianceMethodDetails (stored as separate columns)
        complianceMethodRecordStatus:
          formValues.complianceMethod &&
          formValues.complianceMethod.methodRecordStatus
            ? formValues.complianceMethod.methodRecordStatus
            : null,
        complianceMethodRecord:
          formValues.complianceMethod &&
          formValues.complianceMethod.methodRecord
            ? formValues.complianceMethod.methodRecord
            : null,
        complianceMethodLocation: (function () {
          if (
            !formValues.complianceMethod ||
            !formValues.complianceMethod.methodLocation
          )
            return null;
          const v = formValues.complianceMethod.methodLocation;
          const id = findLocation(v);
          if (id) return { id };
          if (typeof v === "object" && (v.id || v._id))
            return { id: v.id || v._id };
          return null;
        })(),
        complianceMethodTotalTasks:
          formValues.complianceMethod &&
          formValues.complianceMethod.methodTotalTasks
            ? formValues.complianceMethod.methodTotalTasks
            : null,
        // project / regulation project
        projectId:
          formValues.regulationProject &&
          (formValues.regulationProject.project || null),
        projectName:
          formValues.regulationProject &&
          (formValues.regulationProject.projectName || null),
        projectLead:
          formValues.regulationProject &&
          (formValues.regulationProject.projectLead || null),
        projectLocation:
          formValues.regulationProject &&
          (function () {
            const v = formValues.regulationProject.projectLocation;
            if (!v) return null;
            const id = findLocation(v);
            if (id) return { id };
            if (typeof v === "object" && (v.id || v._id))
              return { id: v.id || v._id };
            return null;
          })(),
        projectStatus:
          formValues.regulationProject &&
          (formValues.regulationProject.projectStatus || null),
        projectMilestones:
          formValues.regulationProject &&
          (formValues.regulationProject.milestones || null),
        projectMedia:
          formValues.regulationProject &&
          (formValues.regulationProject.projectMedia || null),
        // task template association (store as id reference)
        taskTemplate:
          general.taskTemplate && typeof general.taskTemplate === "object"
            ? { id: general.taskTemplate.id || general.taskTemplate._id }
            : general.taskTemplate
              ? { id: general.taskTemplate }
              : null,
        // applied from date
        appliedFrom:
          (admin && admin.appliedFrom) ||
          (general && general.appliedFrom) ||
          null,
        // projectRegulationName is not persisted; it's derived from selected source
      };

      // include modifiedAt when editing so backend can validate concurrency
      if (admin && admin.dateModified) {
        payload.modifiedAt = admin.dateModified;
      }

      // associated assets (map to backend shape)
      if (
        formValues.associatedAssets &&
        Array.isArray(formValues.associatedAssets)
      ) {
        payload.associatedAssetsList = formValues.associatedAssets.map((a) => {
          const locId = findLocation(a.assetLocation);
          return {
            id: a.id || a._id || null,
            assetName: a.commonName || a.assetName || null,
            assetType: a.assetType || null,
            location: locId ? { id: locId } : null,
            recordNo: a.assetRecordNo || null,
            recordStatus: a.assetRecordStatus || null,
          };
        });
      }

      // attachments
      if (
        formValues.privateAttachments &&
        Array.isArray(formValues.privateAttachments)
      ) {
        payload.attachmentsList = formValues.privateAttachments.map((att) => ({
          id: att.id || att._id || null,
          filename:
            att.attachmentName || att.filename || att.attachmentUrl || null,
          url: att.attachmentUrl || att.url || null,
        }));
      }

      // location applicabilities -> requirementFacilities
      if (
        formValues.locationApplicabilities &&
        Array.isArray(formValues.locationApplicabilities)
      ) {
        payload.requirementFacilities = formValues.locationApplicabilities.map(
          (la) => {
            const locId = findLocation(la.location, true);
            return {
              id: la.id || null,
              location: locId
                ? { id: locId }
                : typeof la.location === "object"
                  ? la.location
                  : { name: la.location },
              applicable: la.applicable,
              recordNo: la.recordNo,
              justification: la.justification,
            };
          },
        );
      }

      const existingId = formValues._id || formValues.id || null;
      const saved = existingId
        ? await apiPut(`/api/requirements/${existingId}`, payload)
        : await apiPost(`/api/requirements`, payload);

      alert("Requirement saved");
      load();
      navigate("/requirements");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      alert("Save failed: " + err.message);
    }
  }

  return (
    <div className="h-[89.8vh]">
      <Routes>
        <Route
          index
          element={
            <Box sx={{ m: 1 }}>
              <Card>
                <CardContent>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="h6">Requirements</Typography>
                    <div>
                      {user &&
                        [permissions.COMPLIANCE_CAN_ADD_REQUIREMENTS].some(
                          (authority) =>
                            user.authorities === undefined
                              ? false
                              : user.authorities.includes(authority),
                        ) && (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => navigate("/requirements/new")}
                          >
                            Add New Requirement
                          </Button>
                        )}
                    </div>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <DataTable
                      schema={reqSchema}
                      rows={reqs}
                      onFiltersChange={setFilters}
                      onPageSizeChange={handlePageSizeChange}
                      extraControls={null}
                      totalCount={reqsPage.total}
                      currentPage={reqsPage.page}
                      onPageChange={handlePageChange}
                      onDelete={async (index) => {
                        try {
                          const row = reqs[index];
                          const id = row?.id || row?._id || null;
                          if (!id) {
                            showError(
                              "Cannot determine requirement id to delete",
                            );
                            return;
                          }
                          const ok = await showConfirm(
                            `Delete requirement '${row.title || row.summary || id}'? This cannot be undone.`,
                            {
                              title: "Delete Requirement",
                              confirmText: "Delete",
                              cancelText: "Cancel",
                              severity: "error",
                            },
                          );
                          if (!ok) return;
                          await apiDelete(`/api/requirements/${id}`);
                          showSuccess("Requirement deleted");
                          load();
                        } catch (err) {
                          // eslint-disable-next-line no-console
                          console.error(err);
                          showError("Delete failed: " + (err.message || err));
                        }
                      }}
                      onEdit={(idx, row) => {
                        const defaultValues = {
                          _id: row.id || row._id || null,
                          _originalTaskTemplateId: row.taskTemplate?.id || null,
                          _originalTaskRecommended: row.taskRecommended,
                          general: {
                            summary: row.title || row.summary,
                            source:
                              row.sourceComplianceRegulationStandard &&
                              (row.sourceComplianceRegulationStandard.title ||
                                row.sourceComplianceRegulationStandard
                                  .regulationName ||
                                row.sourceComplianceRegulationStandard.name),
                            citation: row.citation,
                            externalLink: row.externalSourceLink,
                            details: row.details || row.description,
                            notes: row.additionalNotes,
                            appliesTo: row.appliesTo,
                            taskRecommended: row.taskRecommended ? "Yes" : "No",
                            taskTemplate: row.taskTemplate
                              ? row.taskTemplate.id
                                ? {
                                    id: row.taskTemplate.id,
                                    name: row.taskTemplate.name,
                                  }
                                : row.taskTemplate
                              : null,
                          },
                          admin: {
                            recordNo: row.recordNo,
                            roaRefId: row.roaReferenceId,
                            appliedFrom: row.appliedFrom || null,
                            recordStatus: row.recordStatus,
                            protected: !!row.protectedFlag,
                            sourceType: row.sourceType,
                            sourceRegion: row.sourceRegion,
                            sourceCountry: row.sourceCountry,
                            area: row.area,
                            createdBy:
                              row.createdBy &&
                              (row.createdBy.username || row.createdBy.name)
                                ? row.createdBy.username || row.createdBy.name
                                : undefined,
                            dateCreated: row.createdAt || undefined,
                            modifiedBy:
                              row.modifiedBy &&
                              (row.modifiedBy.username || row.modifiedBy.name)
                                ? row.modifiedBy.username || row.modifiedBy.name
                                : undefined,
                            dateModified: row.modifiedAt || undefined,
                          },
                          complianceMethod: {
                            methodName: row.complianceMethod || null,
                            methodRecordStatus:
                              row.complianceMethodRecordStatus || null,
                            methodRecord: row.complianceMethodRecord || null,
                            methodLocation:
                              row.complianceMethodLocation &&
                              (row.complianceMethodLocation.name ||
                                row.complianceMethodLocation.code ||
                                row.complianceMethodLocation.id),
                            methodTotalTasks:
                              row.complianceMethodTotalTasks || null,
                          },
                          locationApplicabilities:
                            row.requirementFacilities &&
                            Array.isArray(row.requirementFacilities)
                              ? row.requirementFacilities.map((rf) => ({
                                  id: rf.id || rf.id || null,
                                  location:
                                    rf.location &&
                                    (rf.location.id || rf.location.name)
                                      ? {
                                          id: rf.location.id,
                                          name: rf.location.name,
                                        }
                                      : rf.location,
                                  recordNo: rf.recordNo,
                                  applicable: rf.applicable,
                                  justification: rf.justification,
                                }))
                              : [],
                          associatedAssets:
                            row.associatedAssetsList &&
                            Array.isArray(row.associatedAssetsList)
                              ? row.associatedAssetsList.map((a) => ({
                                  id: a.id || a._id || null,
                                  commonName: a.assetName || null,
                                  assetType: a.assetType || null,
                                  assetLocation:
                                    a.location &&
                                    (a.location.name ||
                                      a.location.code ||
                                      a.location.id)
                                      ? {
                                          id: a.location.id,
                                          name: a.location.name,
                                        }
                                      : a.location,
                                  assetDateCreated: a.createdAt || null,
                                  assetRecordNo: a.recordNo || null,
                                  assetRecordStatus: a.recordStatus || null,
                                }))
                              : [],
                          privateAttachments:
                            row.attachmentsList &&
                            Array.isArray(row.attachmentsList)
                              ? row.attachmentsList.map((att) => ({
                                  id: att.id || att._id || null,
                                  attachmentName: att.filename || null,
                                  attachmentUrl: att.url || null,
                                }))
                              : [],
                          regulationProject: {
                            project: row.projectId || null,
                            projectName: row.projectName || null,
                            projectLead: row.projectLead || null,
                            projectLocation: row.projectLocation
                              ? row.projectLocation.name ||
                                row.projectLocation.code ||
                                row.projectLocation.id ||
                                null
                              : null,
                            projectStatus: row.projectStatus || null,
                            milestones: row.projectMilestones || null,
                            projectMedia: row.projectMedia || null,
                            projectRegulationName:
                              (row.sourceComplianceRegulationStandard &&
                                (row.sourceComplianceRegulationStandard.title ||
                                  row.sourceComplianceRegulationStandard
                                    .regulationName ||
                                  row.sourceComplianceRegulationStandard
                                    .name)) ||
                              null,
                          },
                        };
                        navigate(`/requirements/new`, {
                          state: { defaultValues },
                        });
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
            </Box>
          }
        />
        <Route
          path="new"
          element={
            <Box sx={{ m: 1, maxHeight: "87vh", overflowY: "auto" }}>
              <Card>
                <CardContent>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="h6">New Requirement</Typography>
                  </div>
                  <div
                    style={{ marginTop: 12 }}
                    className="flex justify-center"
                  >
                    <div style={{ width: "100%", maxWidth: 900 }}>
                      <DynamicForm
                        schema={reqSchema}
                        defaultValues={location.state?.defaultValues || {}}
                        onSubmit={handleSave}
                        onCancel={() => navigate("/requirements")}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Box>
          }
        />
      </Routes>
    </div>
  );
}
